import React, { useEffect, useState } from "react";
import GlobalHeader from "../../../components/GlobalHeader";
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  tableCellClasses,
  Paper,
} from "@mui/material";
import styled from "@emotion/styled";
import { LoadingButton } from "@mui/lab";
import useApi from "../../../hooks/useApi";
import useSnack from "../../../hooks/useSnack";
import SnackAlert from "../../../components/SnackAlert";
import Audio from "react-loading-icons/dist/esm/components/audio";
import countries from "../../../utils/countries";
import {
  FaSpotify,
  FaTiktok,
  FaInstagram,
  FaYoutube,
  FaFacebook,
} from "react-icons/fa";
import { hrefSeguro } from "../../../utils/hrefSeguro";
import useEnvios5RC from "./Envios5RC";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    textAlign: "center",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    textAlign: "center",
  },
}));

const REDES = [
  { name: "facebook", Icon: FaFacebook },
  { name: "instagram", Icon: FaInstagram },
  { name: "youtube", Icon: FaYoutube },
  { name: "spotify", Icon: FaSpotify },
  { name: "tiktok", Icon: FaTiktok },
];

const ESTADO_LABELS = {
  pendiente: { label: "Pendiente", color: "warning" },
  verificado: { label: "Verificado", color: "success" },
};

const VerificacionesAdmin = () => {
  const { msgSB, openSB, showSnack, typeSB } = useSnack();
  const { loadApi, loadedApi, loadingApi } = useApi();
  const [difusores, setDifusores] = useState([]);
  const [feedbackModal, setFeedbackModal] = useState(null); // difusor seleccionado
  const [feedbackText, setFeedbackText] = useState("");
  const cincoRC = useEnvios5RC(showSnack);

  useEffect(() => {
    loadApi("admin/verificaciones", true, "get")
      .then((res) => {
        setDifusores(res.data || []);
      })
      .catch((e) => {
        showSnack(e.message, "error", false);
      });
  }, []);

  const cambiarEstado = (difusor, estado) => {
    loadApi(`admin/verificaciones/${difusor.userId}`, true, "put", { estado })
      .then((res) => {
        setDifusores((prev) =>
          prev.map((item) =>
            item.userId === difusor.userId ? { ...item, estado } : item
          )
        );
        showSnack(res.data.message);
      })
      .catch((e) => {
        showSnack(e.message, "error");
      });
  };

  const enviarFeedback = () => {
    const feedback = feedbackText.trim();
    if (!feedback) {
      showSnack("Escribe el feedback para el difusor", "warning");
      return;
    }
    loadApi(
      `admin/verificaciones/${feedbackModal.userId}/feedback`,
      true,
      "post",
      { feedback }
    )
      .then((res) => {
        setDifusores((prev) =>
          prev.map((item) =>
            item.userId === feedbackModal.userId
              ? { ...item, feedback, estado: "pendiente" }
              : item
          )
        );
        setFeedbackModal(null);
        setFeedbackText("");
        showSnack(res.data.message);
      })
      .catch((e) => {
        showSnack(e.message, "error");
      });
  };

  const loading = !loadedApi.includes("get__admin/verificaciones");
  // Las solicitudes de 5 RC por revisar van primero en su tabla.
  const porRevisarPrimero = (items) =>
    [...items].sort((a, b) => cincoRC.porRevisar(b) - cincoRC.porRevisar(a));
  const pendientes = porRevisarPrimero(
    difusores.filter((d) => d.estado === "pendiente")
  );
  const verificados = porRevisarPrimero(
    difusores.filter((d) => d.estado === "verificado")
  );

  const renderTabla = (items) => (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 800 }}>
        <TableHead>
          <TableRow>
            <StyledTableCell>Difusor</StyledTableCell>
            <StyledTableCell>Medio</StyledTableCell>
            <StyledTableCell>Enlaces</StyledTableCell>
            <StyledTableCell>Estado</StyledTableCell>
            <StyledTableCell>Acciones</StyledTableCell>
            <StyledTableCell sx={{ whiteSpace: "nowrap" }}>
              Envío de 5 RC
            </StyledTableCell>
            <StyledTableCell sx={{ whiteSpace: "nowrap" }}>
              Estado 5 RC
            </StyledTableCell>
            <StyledTableCell sx={{ whiteSpace: "nowrap" }}>
              Acciones 5 RC
            </StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((difusor) => (
            <TableRow key={difusor.userId}>
              <StyledTableCell sx={{ textAlign: "left" }}>
                <Typography variant="body2" fontWeight={600}>
                  {difusor.nombre}
                </Typography>
                <Typography variant="caption" color="GrayText">
                  {difusor.email}
                </Typography>
              </StyledTableCell>
              <StyledTableCell sx={{ textAlign: "left" }}>
                <Typography variant="body2">{difusor.nombre_media}</Typography>
                <Typography variant="caption" color="GrayText">
                  {difusor.tipo}
                  {difusor.pais ? ` · ${countries[difusor.pais]}` : ""}
                </Typography>
              </StyledTableCell>
              <StyledTableCell>
                <Stack alignItems="center" gap={0.5}>
                  {difusor.pagina_web && (
                    <Link
                      href={hrefSeguro(difusor.pagina_web)}
                      target="_blank"
                      rel="nofollow noreferrer"
                      variant="body2"
                    >
                      Página web
                    </Link>
                  )}
                  <Stack direction="row" gap={0.5} justifyContent="center">
                    {REDES.filter(({ name }) => difusor[name]).map(
                      ({ name, Icon }) => (
                        <Link
                          key={name}
                          href={hrefSeguro(difusor[name])}
                          target="_blank"
                          rel="nofollow noreferrer"
                          sx={{ color: "#666", ":hover": { color: "#F27224" } }}
                        >
                          <Icon />
                        </Link>
                      )
                    )}
                  </Stack>
                </Stack>
              </StyledTableCell>
              <StyledTableCell>
                <Chip
                  label={ESTADO_LABELS[difusor.estado]?.label || difusor.estado}
                  color={ESTADO_LABELS[difusor.estado]?.color || "default"}
                  size="small"
                />
                {difusor.feedback && (
                  <Typography
                    variant="caption"
                    color="GrayText"
                    display="block"
                    mt={0.5}
                  >
                    Último feedback enviado
                  </Typography>
                )}
              </StyledTableCell>
              <StyledTableCell>
                <Stack
                  direction="row"
                  gap={1}
                  justifyContent="center"
                  sx={{ "& .MuiButton-root": { whiteSpace: "nowrap" } }}
                >
                  {difusor.estado === "pendiente" ? (
                    <LoadingButton
                      size="small"
                      color="success"
                      loading={loadingApi.includes(
                        `put__admin/verificaciones/${difusor.userId}`
                      )}
                      onClick={() => cambiarEstado(difusor, "verificado")}
                    >
                      Verificar
                    </LoadingButton>
                  ) : (
                    <LoadingButton
                      size="small"
                      color="warning"
                      variant="outlined"
                      loading={loadingApi.includes(
                        `put__admin/verificaciones/${difusor.userId}`
                      )}
                      onClick={() => cambiarEstado(difusor, "pendiente")}
                    >
                      Quitar verificación
                    </LoadingButton>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setFeedbackModal(difusor);
                      setFeedbackText("");
                    }}
                  >
                    Feedback
                  </Button>
                </Stack>
              </StyledTableCell>
              <StyledTableCell>{cincoRC.renderPlan(difusor)}</StyledTableCell>
              <StyledTableCell>{cincoRC.renderEstado(difusor)}</StyledTableCell>
              <StyledTableCell>{cincoRC.renderAcciones(difusor)}</StyledTableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      <GlobalHeader />
      {/* Más ancho que el resto del admin: la tabla suma las columnas de 5 RC. */}
      <Container maxWidth="xl" sx={{ pb: 6 }}>
        <Stack gap={3} pt={2}>
          <Typography variant="h4" color="primary.main" fontWeight="900">
            Verificación de Difusores
          </Typography>
          {cincoRC.cuantosPorRevisar > 0 && (
            <Typography variant="body1">
              {cincoRC.cuantosPorRevisar === 1
                ? "1 solicitud de envíos de 5 RC por revisar: aparece"
                : `${cincoRC.cuantosPorRevisar} solicitudes de envíos de 5 RC por revisar: aparecen`}{" "}
              primero en su tabla.
            </Typography>
          )}
          {loading ? (
            <Stack alignItems="center" py={8}>
              <Audio fill="#F27224" />
            </Stack>
          ) : (
            <>
              <Typography variant="h5">
                Pendientes ({pendientes.length})
              </Typography>
              {pendientes.length === 0 ? (
                <Typography variant="body1" color="GrayText">
                  No hay difusores pendientes de verificación
                </Typography>
              ) : (
                renderTabla(pendientes)
              )}
              <Typography variant="h5">
                Verificados ({verificados.length})
              </Typography>
              {verificados.length === 0 ? (
                <Typography variant="body1" color="GrayText">
                  Aún no hay difusores verificados
                </Typography>
              ) : (
                renderTabla(verificados)
              )}
            </>
          )}
        </Stack>
      </Container>
      {/* Modal de feedback */}
      <Dialog
        open={Boolean(feedbackModal)}
        onClose={() => setFeedbackModal(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Feedback para {feedbackModal?.nombre_media || feedbackModal?.nombre}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="GrayText" mb={2}>
            El difusor recibirá estos comentarios por correo y notificación
            para completar la verificación de su medio.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="Ej: El link de tu página web no funciona; agrega tus redes sociales para poder validar tu medio..."
          />
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button variant="text" onClick={() => setFeedbackModal(null)}>
            Cancelar
          </Button>
          <LoadingButton
            loading={loadingApi.includes(
              `post__admin/verificaciones/${feedbackModal?.userId}/feedback`
            )}
            onClick={enviarFeedback}
          >
            Enviar feedback
          </LoadingButton>
        </DialogActions>
      </Dialog>
      {cincoRC.dialogos}
      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </>
  );
};

export default VerificacionesAdmin;
